import { Button } from "@/components/ui";
import { setBudgetData } from "@/redux/slices";
import { routes } from "@/routes/utils";
import {
  useApproveMutation,
  useGetBudgetById,
  useGetCommentsByBudgetId,
  useGetLineItemsByBudgetId,
  useRejectMutation,
} from "@/services";
import { Check, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { autoApprovalLevelOptions } from "./consts";
import { toast } from "@/components/Toast";
import { RootState } from "@/redux/store";
import { actionsKeys, formatAmount, permissionActions } from "@/lib/utils";

const useReviewBudget = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { budgetId, action } = useParams();
  const [openDistributionDialog, setOpenDistributionDialog] =
    useState<boolean>(false);
  const [rowSelected, setRowSelected] = useState<any>(null);
  const [openApproveRejectDialog, setOpenApproveRejectDialog] =
    useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const isTolerance = location?.pathname?.includes("tolerance");
  const isView = action == "view";
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const { data: budgetInfo, isLoading: isBudgetDataLoading } = useGetBudgetById(
    {
      communityId: communityDetail?.communityId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && budgetId),
      },
    }
  );

  const { data: lineItems, isLoading: isLineItemDataLoading } =
    useGetLineItemsByBudgetId({
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!budgetId,
      },
    });

  const { data: commentsData, isLoading: isCommentsLoading } =
    useGetCommentsByBudgetId({
      communityId: communityDetail?.communityId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && budgetId),
      },
    });

  const { mutate: approveInfo, isPending: isApprovePending } =
    useApproveMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        onBackClick();
        toast.success("Budget Approved");
      },
    });

  const { mutate: rejectInfo, isPending: isRejectPending } = useRejectMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      onBackClick();
      toast.success("Budget Rejected");
    },
  });

  useEffect(() => {
    if (budgetInfo) {
      dispatch(setBudgetData(budgetInfo?.data));
    }
  }, [budgetInfo]);

  const breadcrumbDataBudget = [
    {
      link: `/${routes.bom}/${routes.finance}`,
      name: `Approvals`,
    },
    {
      link: `/${routes.bom}/${routes.finance}/${routes.budgetApprovals}`,
      name: <span>{"Budget Approvals"}</span>,
    },
    {
      link: "",
      name: (
        <span className="text-primary">{budgetInfo?.data?.budgetName}</span>
      ),
    },
  ];

  const breadcrumbDataTolerance = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.payment}`,
        name: <span>{"Payment"}</span>,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.payment}/${routes.tolerance}`,
        name: <span>Tolerance 360 Report (Year - {currentFinanceYear})</span>,
      },
      {
        link: ``,
        name: (
          <span className="text-primary">{budgetInfo?.data?.budgetName}</span>
        ),
      },
    ],
    []
  );

  const breadcrumbData = isTolerance
    ? breadcrumbDataTolerance
    : breadcrumbDataBudget;

  const onBackClickBudget = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.budgetApprovals}`);
  };

  const onBackClickTolerance = () => {
    navigate(
      `/${routes.bom}/${routes.finance}/${routes.payment}/${routes.tolerance}`
    );
  };

  const onBackClick = isTolerance ? onBackClickTolerance : onBackClickBudget;
  // Define Table Columns
  const columns = [
    {
      accessorKey: "budgetLineitemId",
      header: "Line Item ID",
      accessorFn: (row: any) => String(row.budgetLineitemId || ""),
    },
    {
      accessorKey: "glAccountNumber",
      header: "GL Account No",
      accessorFn: (row: any) =>
        String(
          `${row?.glAccountNumber}-${row?.glMasterAccountShortDesc}` || ""
        ),
    },
    {
      accessorKey: "budgetTransactionRate",
      meta: { align: "right" },
      header: <div>Rate</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.budgetTransactionRate) || "0"),
    },
    {
      accessorKey: "budgetedQuantity",
      header: <div>Quantity</div>,
      accessorFn: (row: any) => String(row?.budgetedQuantity) || "",
    },
    {
      accessorKey: "budgetYearlyAmt",
      header: <div>Annual Budget</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.budgetYearlyAmt) || ""),
    },
    {
      accessorKey: "autoApproval",
      header: <div>Auto Invoice Approval</div>,
      cell: (row: any) =>
        row?.row?.original?.autoApproval ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "autoApprovalLevel",
      header: <div>Auto Invoice Approval Level</div>,
      accessorFn: (row: any) =>
        String((autoApprovalLevelOptions as any)[row?.autoApprovalLevel] || ""),
    },
    {
      accessorKey: "",
      header: " ",
      cell: (row: any) => (
        <Button
          type="button"
          variant="ghost"
          className="shadow-none w-fit p-0 text-[12px] h-[20px] text-primary border-b-primary border-b-1 rounded-none hover:bg-transparent hover:text-primary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setRowSelected(row.row.original);
            setOpenDistributionDialog(true);
          }}
        >
          Monthly Distribution
        </Button>
      ),
    },
  ];

  const onApproveClick = () => {
    setOpenApproveRejectDialog(true);
    setIsApproved(true);
  };

  const onRejectClick = () => {
    setOpenApproveRejectDialog(true);
    setIsApproved(false);
  };

  const onApproveConfirm = (reason: string) => {
    approveInfo({
      communityId: communityDetail?.communityId as string,
      budgetId: budgetId as string,
      reason,
    });
  };

  const onRejectConfirm = (reason: string) => {
    rejectInfo({
      communityId: communityDetail?.communityId as string,
      budgetId: budgetId as string,
      reason,
    });
  };

  const onClose = () => {
    setOpenApproveRejectDialog(false);
    setIsApproved(false);
  };

  const hasApprovePermission = useMemo(() => accessPermission?.[actionsKeys?.BUDGET?.toLowerCase()][permissionActions.APPROVE], [accessPermission]);

  const hideApproveRejectBtn = () => {
    const loggedInUser = budgetInfo?.data?.budgetApprovals?.some(
      (data: any) => data.createdBy == loggedInUserData?.userId
    );

    return !loggedInUser && hasApprovePermission;
  };

  return {
    breadcrumbData,
    onBackClick,
    columns,
    budgetInfo,
    isBudgetDataLoading,
    lineItems,
    isLineItemDataLoading,
    commentsData,
    isCommentsLoading,
    openDistributionDialog,
    setOpenDistributionDialog,
    rowSelected,
    onApproveClick,
    onRejectClick,
    onApproveConfirm,
    onRejectConfirm,
    openApproveRejectDialog,
    isApproved,
    isApprovePending,
    isRejectPending,
    onClose,
    communityDetail,
    hideApproveRejectBtn,
    isView,
  };
};

export default useReviewBudget;
